import { useId, useRef, useState } from 'react';
import { Controller, useForm, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Checkbox, Form, Input, Label, Modal, Select } from '@shared/components';
import { useChangeVehicleRegistrationMutation } from '@/features/vehiclesearch/hooks/useChangeVehicleRegistrationMutation';
import {
    registrationChangeSchema,
    type RegistrationChangeFormInput,
    type RegistrationChangeFormValues,
} from '@/features/vehiclesearch/schemas/registrationChange.schema';
import { REGISTRATION_CHANGE_REASONS } from '@/features/vehiclesearch/types/vehicleRegistration.types';
import { REGISTRATION_CHANGE_REASON_LABEL } from '@/features/vehiclesearch/utils/registrationChangeLabels';
import { mapRegistrationSubmitError } from '../../../utils/mapRegistrationSubmitError';

interface Props {
    agreementId: string;
    vehicleId: string;
    isOpen: boolean;
    onClose: () => void;
}

const REASON_OPTIONS = REGISTRATION_CHANGE_REASONS.map((value) => ({
    value,
    label: REGISTRATION_CHANGE_REASON_LABEL[value],
}));

const EMPTY_FORM: DefaultValues<RegistrationChangeFormInput> = { newVrm: '' };

export function ChangeRegistrationModal({ agreementId, vehicleId, isOpen, onClose }: Props) {
    const headingId = useId();
    const ids = {
        newVrm: `${headingId}-new-vrm`,
        reason: `${headingId}-reason`,
        sameVehicle: `${headingId}-same-vehicle`,
    };
    const newVrmRef = useRef<HTMLDivElement>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const mutation = useChangeVehicleRegistrationMutation(vehicleId);

    const {
        control,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<RegistrationChangeFormInput, unknown, RegistrationChangeFormValues>({
        resolver: zodResolver(registrationChangeSchema),
        mode: 'onBlur',
        defaultValues: EMPTY_FORM,
    });

    const handleClose = () => {
        if (mutation.isPending) return;
        setFormError(null);
        clearErrors();
        onClose();
    };

    const handleValid = (values: RegistrationChangeFormValues) => {
        setFormError(null);
        mutation.mutate(
            { ...values, agreementId },
            {
                onSuccess: () => {
                    reset(EMPTY_FORM);
                    onClose();
                },
                onError: (error: unknown) => {
                    const { fieldErrors, message } = mapRegistrationSubmitError(error);
                    fieldErrors.forEach(({ field, message: fieldMessage }) => setError(field, { message: fieldMessage }));
                    setFormError(message);
                },
            },
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            showCloseButton={!mutation.isPending}
            ariaLabelledBy={headingId}
            className="max-w-lg p-6"
            initialFocusRef={newVrmRef}
        >
            <h2 id={headingId} className="mb-4 text-lg font-semibold">
                Change registration
            </h2>
            <Form onSubmit={handleSubmit(handleValid)} noValidate className="space-y-4">
                {formError && <Alert variant="error">{formError}</Alert>}
                <div>
                    <Label htmlFor={ids.newVrm}>New registration</Label>
                    <Controller
                        name="newVrm"
                        control={control}
                        render={({ field }) => (
                            <div ref={newVrmRef}>
                                <Input
                                    id={ids.newVrm}
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!errors.newVrm}
                                    aria-invalid={!!errors.newVrm}
                                    aria-describedby={errors.newVrm ? `${ids.newVrm}-error` : undefined}
                                    disabled={mutation.isPending}
                                />
                            </div>
                        )}
                    />
                    <FieldError id={`${ids.newVrm}-error`} message={errors.newVrm?.message} />
                </div>

                <div>
                    <Label htmlFor={ids.reason}>Why is it changing?</Label>
                    <Controller
                        name="reason"
                        control={control}
                        render={({ field }) => (
                            <Select
                                id={ids.reason}
                                ariaLabel="Why is it changing?"
                                placeholder="Choose a reason"
                                options={REASON_OPTIONS}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!errors.reason}
                                ariaDescribedBy={errors.reason ? `${ids.reason}-error` : undefined}
                                disabled={mutation.isPending}
                            />
                        )}
                    />
                    <FieldError id={`${ids.reason}-error`} message={errors.reason?.message} />
                </div>

                <div>
                    <Controller
                        name="sameVehicleConfirmed"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                id={ids.sameVehicle}
                                label="This is the same vehicle and the VIN has not changed"
                                checked={field.value === true}
                                onChange={(checked) => field.onChange(checked ? true : undefined)}
                                ariaLabel="This is the same vehicle and the VIN has not changed"
                                disabled={mutation.isPending}
                            />
                        )}
                    />
                    <FieldError id={`${ids.sameVehicle}-error`} message={errors.sameVehicleConfirmed?.message} />
                </div>

                <div className="flex justify-end gap-2">
                    <Button type="button" variant="clear" disabled={mutation.isPending} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={mutation.isPending}>
                        {mutation.isPending ? 'Saving…' : 'Change registration'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}

function FieldError({ id, message }: { id: string; message?: string }) {
    if (!message) return null;

    return (
        <p id={id} role="alert" className="mt-1.5 text-xs text-mp-error">
            {message}
        </p>
    );
}
